import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
// @ts-expect-error: ignoring as it is existing js file
import InputMask from "react-input-mask";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
// import Typography from "@mui/material/Typography";
import { Button, FormControl, InputLabel, MenuItem, Select, Box } from "@mui/material";
import { Typography } from "@financeable-com-au/financeable-ui";
import { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { saveValue, userDetailsSelector } from "@/store/slices/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { NumericFormatCustom, currencyMaskedValue } from "../../utils/currencyMaskFormat";
import { frequencyList } from "../../constants";

interface FinancialCommitment {
  id: string;
  dateOfPurchase: string;
  description: string;
  financeCompany: string;
  purchasePrice: string;
  repaymentFrequency: string;
  contractTermMonths: string;
  contractExpiryDate: string;
  repaymentsMonthly: string;
}

const EntityFinancialCommitments = React.memo(
  ({ applicationId, entityId }: { applicationId?: string; entityId?: string }) => {
    const dispatch = useDispatch();
    const { demoLoanDetailsFields } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
    const [allValues, setAllValue] = useState({
      businessBackground: "",
    });

    const [commitments, setCommitments] = useState<FinancialCommitment[]>([
      {
        id: `commitment-${Date.now()}`,
        dateOfPurchase: "",
        description: "",
        financeCompany: "",
        purchasePrice: "",
        repaymentFrequency: "",
        contractTermMonths: "",
        contractExpiryDate: "",
        repaymentsMonthly: "",
      },
    ]);

    const handleAddCommitment = () => {
      const newCommitment: FinancialCommitment = {
        id: `commitment-${Date.now()}-${Math.random()}`,
        dateOfPurchase: "",
        description: "",
        financeCompany: "",
        purchasePrice: "",
        repaymentFrequency: "",
        contractTermMonths: "",
        contractExpiryDate: "",
        repaymentsMonthly: "",
      };
      const updatedCommitments = [...commitments, newCommitment];
      setCommitments(updatedCommitments);
      dispatch(
        saveValue({
          demoLoanDetailsFields: { ...demoLoanDetailsFields, commitments: updatedCommitments },
        }),
      );
    };

    const handleRemoveCommitment = (id: string) => {
      if (commitments.length > 1) {
        const updatedCommitments = commitments.filter((commitment) => commitment.id !== id);
        setCommitments(updatedCommitments);
        dispatch(
          saveValue({
            demoLoanDetailsFields: { ...demoLoanDetailsFields, commitments: updatedCommitments },
          }),
        );
      }
    };

    const handleFieldChange = (id: string, field: keyof FinancialCommitment, value: string) => {
      setCommitments(
        commitments.map((commitment) => (commitment.id === id ? { ...commitment, [field]: value } : commitment)),
      );
    };

    // Calculate totals
    const totals = useMemo(() => {
      const grossMonthlySum = commitments.reduce((sum: number, commitment: FinancialCommitment) => {
        const monthlyRepayment = parseFloat(commitment.repaymentsMonthly?.replace(/[$,]/g, "") || "0");
        if (commitment.repaymentFrequency === "Monthly") {
          return sum + monthlyRepayment;
        } else if (commitment.repaymentFrequency === "Fortnightly") {
          return sum + monthlyRepayment * 2.1667; // Convert fortnightly to monthly: 26/12
        } else if (commitment.repaymentFrequency === "Weekly") {
          return sum + monthlyRepayment * 4.3333; // Convert weekly to monthly: 52/12
        }
        return sum; // Default return if frequency is not set
      }, 0);

      const yearly = grossMonthlySum * 12;

      return {
        grossMonthlySum,
        yearly,
      };
    }, [commitments]);

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const handle = {
      onChangeField: (value: string, key: string) => {
        setAllValue({ ...allValues, [key]: value });
      },
      onBlurField: (key: string, value: string) => {
        dispatch(
          saveValue({
            demoLoanDetailsFields: { ...demoLoanDetailsFields, [key]: value },
          }),
        );
      },
      onBlurCommitments: () => {
        dispatch(
          saveValue({
            demoLoanDetailsFields: { ...demoLoanDetailsFields, commitments: commitments },
          }),
        );
      },
    };

    return (
      <Grid
        container
        spacing={2}
        style={{
          padding: "10px 0 10px 0",
        }}
      >
        <Grid item xs={12}>
          <Typography level="title-md" style={{ marginBottom: "10px" }}>
            Financial Commitments
          </Typography>
        </Grid>

        {commitments.map((commitment, index) => (
          <Grid container item spacing={2} key={commitment.id} style={{ marginBottom: "10px" }} alignItems="center">
            <Grid container item xs={12} spacing={1}>
              {/* Date of Purchase */}
              <Grid item xs={12} sm={3}>
                <InputMask
                  mask="99-99-9999"
                  value={commitment.dateOfPurchase || ""}
                  maskChar={null}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange(commitment.id, "dateOfPurchase", event.target.value)
                  }
                  onBlur={() => handle.onBlurCommitments()}
                >
                  {() => (
                    <TextField
                      label="Date of Purchase"
                      size="small"
                      fullWidth
                      placeholder="DD-MM-YYYY"
                      variant="filled"
                      name={`dateOfPurchase_${commitment.id}`}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </InputMask>
              </Grid>

              {/* Purchase Price */}
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`purchasePrice-input-${commitment.id}`}
                  type="text"
                  inputProps={{ min: 0 }}
                  label="Purchase Price"
                  variant="filled"
                  fullWidth
                  value={commitment.purchasePrice}
                  name={`purchasePrice_${commitment.id}`}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                  }}
                  onChange={(event) =>
                    handleFieldChange(commitment.id, "purchasePrice", currencyMaskedValue(event.target.value))
                  }
                  onBlur={() => handle.onBlurCommitments()}
                />
              </Grid>

              {/* Repayments (Monthly) */}
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`repaymentsMonthly-input-${commitment.id}`}
                  type="text"
                  inputProps={{ min: 0 }}
                  label="Repayments (Monthly)"
                  variant="filled"
                  fullWidth
                  value={commitment.repaymentsMonthly}
                  name={`repaymentsMonthly_${commitment.id}`}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                  }}
                  onChange={(event) =>
                    handleFieldChange(commitment.id, "repaymentsMonthly", currencyMaskedValue(event.target.value))
                  }
                  onBlur={() => handle.onBlurCommitments()}
                />
              </Grid>

              {/* Repayment Frequency */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled">
                  <InputLabel id={`repaymentFrequency-label-${commitment.id}`}>Repayment Frequency</InputLabel>
                  <Select
                    size="small"
                    labelId={`repaymentFrequency-label-${commitment.id}`}
                    id={`repaymentFrequency-input-${commitment.id}`}
                    value={commitment.repaymentFrequency}
                    label="Repayment Frequency"
                    name={`repaymentFrequency_${commitment.id}`}
                    onChange={(event) => handleFieldChange(commitment.id, "repaymentFrequency", event.target.value)}
                    onBlur={() => handle.onBlurCommitments()}
                  >
                    {frequencyList.map((freq) => (
                      <MenuItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Finance Company */}
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`financeCompany-input-${commitment.id}`}
                  type="text"
                  label="Finance Company"
                  variant="filled"
                  fullWidth
                  value={commitment.financeCompany}
                  name={`financeCompany_${commitment.id}`}
                  onChange={(event) => handleFieldChange(commitment.id, "financeCompany", event.target.value)}
                  onBlur={() => handle.onBlurCommitments()}
                />
              </Grid>

              {/* Contract Term (Months) */}
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`contractTerm-input-${commitment.id}`}
                  type="number"
                  inputProps={{ min: 0 }}
                  label="Contract Term (Months)"
                  variant="filled"
                  fullWidth
                  value={commitment.contractTermMonths}
                  name={`contractTermMonths_${commitment.id}`}
                  onChange={(event) => handleFieldChange(commitment.id, "contractTermMonths", event.target.value)}
                  onBlur={() => handle.onBlurCommitments()}
                />
              </Grid>

              {/* Contract Expiry Date */}
              <Grid item xs={12} sm={3}>
                <InputMask
                  mask="99-99-9999"
                  value={commitment.contractExpiryDate || ""}
                  maskChar={null}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange(commitment.id, "contractExpiryDate", event.target.value)
                  }
                  onBlur={() => handle.onBlurCommitments()}
                >
                  {() => (
                    <TextField
                      label="Contract Expiry Date"
                      size="small"
                      fullWidth
                      placeholder="DD-MM-YYYY"
                      variant="filled"
                      name={`contractExpiryDate_${commitment.id}`}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </InputMask>
              </Grid>

              {/* Description */}
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`description-input-${commitment.id}`}
                  type="text"
                  label="Description"
                  variant="filled"
                  fullWidth
                  value={commitment.description}
                  name={`description_${commitment.id}`}
                  onChange={(event) => handleFieldChange(commitment.id, "description", event.target.value)}
                  onBlur={() => handle.onBlurCommitments()}
                />
              </Grid>
            </Grid>

            {/* Remove Button */}
            <Grid item xs={12} sm={1}>
              {commitments.length > 1 && index !== 0 && (
                <Button size="small" onClick={() => handleRemoveCommitment(commitment.id)} variant="outlined">
                  REMOVE
                </Button>
              )}
            </Grid>
          </Grid>
        ))}

        {/* Add Button */}
        <Grid item xs={12}>
          <Button
            onClick={handleAddCommitment}
            variant="outlined"
            size="small"
            disabled={
              !commitments[0]?.dateOfPurchase &&
              !commitments[0]?.description &&
              !commitments[0]?.financeCompany &&
              !commitments[0]?.purchasePrice &&
              !commitments[0]?.repaymentFrequency &&
              !commitments[0]?.contractTermMonths &&
              !commitments[0]?.contractExpiryDate &&
              !commitments[0]?.repaymentsMonthly
            }
          >
            Add Commitment
          </Button>
        </Grid>

        {/* Summary Section */}
        <Grid item xs={12}>
          <Box
            style={{
              margin: "10px 0 30px 0",
              borderRadius: "4px",
            }}
          >
            <Typography
              level="body-lg"
              bold
              style={{
                marginBottom: "18px",
              }}
            >
              Commitment Schedule Summary
            </Typography>
            <Grid container spacing={2} sm={6}>
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <Typography level="body-md">Gross Monthly Sum:</Typography>
                  </Grid>
                  <Grid item xs={6} style={{ textAlign: "right" }}>
                    <Typography level="body-md" bold>
                      {formatCurrency(totals.grossMonthlySum)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <Typography level="body-md">Yearly:</Typography>
                  </Grid>
                  <Grid item xs={6} style={{ textAlign: "right" }}>
                    <Typography level="body-md" bold>
                      {formatCurrency(totals.yearly)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="businessBackground"
              label="Business Background"
              variant="filled"
              type="text"
              size="small"
              name="businessBackground"
              multiline
              minRows={5}
              maxRows={10}
              value={allValues?.businessBackground}
              onChange={(event) => handle.onChangeField(event?.target?.value, "businessBackground")}
              onBlur={(event) => handle.onBlurField("businessBackground", allValues?.businessBackground)}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  },
);

EntityFinancialCommitments.displayName = "EntityFinancialCommitments";

export default EntityFinancialCommitments;
